import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast-with-sound";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Share2, Copy, Check, MessageCircle, Facebook, Twitter, Send } from "lucide-react";
import { generateShareUrl, slugify } from "@/utils/slugify";
import { getTranslatedValue } from "@/components/TranslatableField";
import { BACKEND_URL } from "../config";

/**
 * ShareModal - Reusable share component
 * 
 * @param {string} type - Type of content: 'site', 'trip', 'motard', 'club', 'motorcycle'
 * @param {object} data - Data for sharing (varies by type)
 *   - site: {} (no data needed)
 *   - trip: { id, title }
 *   - motard: { id, nickname }
 *   - group: { id, name }
 *   - motorcycle: { id, name }
 * @param {string} variant - Button variant (default: "outline")
 * @param {string} size - Button size (default: "sm")
 * @param {string} className - Additional CSS classes
 * @param {boolean} iconOnly - Show only icon without text
 * @param {boolean} noTooltip - Disable internal tooltip (use when wrapping with external tooltip)
 * @param {React.ReactNode} customTrigger - Custom trigger element (optional)
 */
export function ShareModal({ 
  type = "site", 
  data = {}, 
  variant = "outline",
  size = "sm",
  className = "",
  iconOnly = false,
  buttonText = null,
  customTrigger = null,
  noTooltip = false
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate URL and message based on type
  const getShareInfo = () => {
    const baseUrl = window.location.origin;
    const apiUrl = BACKEND_URL || baseUrl;
    
    switch (type) {
      case "trip":
        return {
          url: generateShareUrl("/passeio", data.id, data.title),
          ogUrl: `${apiUrl}/api/og/passeio/${data.id}/${slugify(getTranslatedValue(data.title, "pt") || "passeio")}`,
          title: `${getTranslatedValue(data.title, "pt") || "Passeio"} | MotoTrips`,
          message: t("seo_dynamic.share_trip", { title: getTranslatedValue(data.title, currentLang) || "" }),
        };
      case "event":
        return {
          url: generateShareUrl("/evento", data.id, data.title),
          ogUrl: `${apiUrl}/api/og/evento/${data.id}/${slugify(getTranslatedValue(data.title, "pt") || "evento")}`,
          title: `${getTranslatedValue(data.title, "pt") || "Evento"} | MotoTrips`,
          message: t("seo_dynamic.share_event", {
            title: getTranslatedValue(data.title, currentLang) || "",
            defaultValue: "Vê este evento na MotoTrips: {{title}}",
          }),
        };
      case "route":
        return {
          url: `${baseUrl}${data.path}`,
          ogUrl: `${apiUrl}/api/og${data.path}`,
          title: `${data.name || "Rota"} | MotoTrips`,
          message: t("seo_dynamic.share_route", { name: data.name || "" }),
        };
      case "motard":
        return {
          url: `${baseUrl}/meu-canto?motard=${data.id}`,
          ogUrl: `${apiUrl}/api/og/motard/${data.id}/${slugify(data.nickname || "motard")}`,
          title: `${data.nickname} | MotoTrips`,
          message: t("seo_dynamic.share_motard", { name: data.nickname || "" }),
        };
      case "club":
        return {
          url: generateShareUrl("/clube", data.id, data.name),
          ogUrl: `${apiUrl}/api/og/clube/${data.id}/${slugify(getTranslatedValue(data.name, "pt") || "clube")}`,
          title: `${getTranslatedValue(data.name, "pt") || "Clube"} | MotoTrips`,
          message: t("seo_dynamic.share_club", { name: getTranslatedValue(data.name, currentLang) || "" }),
        };
      case "motorcycle":
        return {
          url: `${baseUrl}/mota/${data.id}/${slugify(getTranslatedValue(data.name, "pt") || "mota")}`,
          ogUrl: `${apiUrl}/api/og/mota/${data.id}/${slugify(getTranslatedValue(data.name, "pt") || "mota")}`,
          title: `${getTranslatedValue(data.name, "pt") || "Mota"} | MotoTrips`,
          message: t("seo_dynamic.share_moto", { name: getTranslatedValue(data.name, currentLang) || "" }),
        };
      case "garage":
        return {
          url: `${baseUrl}/garagem?motard=${data.id}`,
          ogUrl: `${apiUrl}/api/og/motard/${data.id}/${slugify(data.nickname || "garagem")}`,
          title: `Garagem de ${data.nickname || "Motard"} | MotoTrips`,
          message: t("seo_dynamic.share_garage", { name: data.nickname || "" }),
        };
      case "site":
      default:
        return {
          url: baseUrl,
          ogUrl: baseUrl,
          title: "MotoTrips — Planeia, Regista e Partilha os Teus Passeios de Mota!",
          message: t("seo_dynamic.site_share_message"),
        };
    }
  };

  const shareInfoRaw = getShareInfo();
  // Carry the current language into shared links via ?lng= (pt = clean URL)
  // so the recipient opens the page in the same language as the sharer.
  const withLng = (u) => {
    if (!u || currentLang === "pt" || !["en", "es", "fr"].includes(currentLang)) return u;
    return u + (u.includes("?") ? "&" : "?") + "lng=" + currentLang;
  };
  const shareInfo = {
    ...shareInfoRaw,
    url: withLng(shareInfoRaw.url),
    ogUrl: withLng(shareInfoRaw.ogUrl),
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareInfo.url);
      setCopied(true);
      toast.success(t("toasts.link_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t("toasts.link_copy_error"));
    }
  };

  // Share via WhatsApp - uses ogUrl for better previews
  const handleWhatsApp = () => {
    // Use ogUrl which has Open Graph meta tags for rich previews
    const shareUrl = shareInfo.ogUrl || shareInfo.url;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareInfo.message + "\n\n" + shareUrl)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const handleFacebook = () =>
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareInfo.ogUrl || shareInfo.url)}`);
  const handleTwitter = () =>
    openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareInfo.message)}&url=${encodeURIComponent(shareInfo.ogUrl || shareInfo.url)}`);
  const handleTelegram = () =>
    openShare(`https://t.me/share/url?url=${encodeURIComponent(shareInfo.ogUrl || shareInfo.url)}&text=${encodeURIComponent(shareInfo.message)}`);

  // Get button label based on type
  const getButtonLabel = () => {
    if (iconOnly) return null;
    if (buttonText) return buttonText;
    return t("actions.share");
  };

  // Get modal title based on type
  const getModalTitle = () => {
    return t("actions.share");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {noTooltip ? (
        <DialogTrigger asChild>
          {customTrigger || (
            <Button 
              variant={variant} 
              size={size} 
              className={`gap-2 ${className}`}
              data-testid={`share-button-${type}`}
            >
              <Share2 className={iconOnly ? "h-5 w-5" : "h-4 w-4"} />
              {getButtonLabel()}
            </Button>
          )}
        </DialogTrigger>
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                {customTrigger || (
                  <Button 
                    variant={variant} 
                    size={size} 
                    className={`gap-2 ${className}`}
                    data-testid={`share-button-${type}`}
                  >
                    <Share2 className={iconOnly ? "h-5 w-5" : "h-4 w-4"} />
                    {getButtonLabel()}
                  </Button>
                )}
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="z-[10000]">
              <p>{type === "site" ? t("header.tooltip_share_site") : type === "trip" ? t("header.tooltip_share_trip") : t("header.tooltip_share")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            {getModalTitle()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* URL Preview */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">{t("actions.share")}:</p>
            <p className="text-sm font-mono break-all">{shareInfo.url}</p>
          </div>

          {/* Share Options */}
          <div className="grid grid-cols-3 gap-3">
            {/* Copy Link */}
            <Button
              variant="outline"
              className="h-16 flex-col gap-2"
              onClick={handleCopyLink}
              data-testid="share-copy-link"
            >
              {copied ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <Copy className="h-5 w-5" />
              )}
              <span className="text-xs">{copied ? t("actions.copied") : t("actions.copy")}</span>
            </Button>

            {/* WhatsApp */}
            <Button
              variant="outline"
              className="h-16 flex-col gap-2 hover:bg-green-500/10 hover:border-green-500/50"
              onClick={handleWhatsApp}
              data-testid="share-whatsapp"
            >
              <MessageCircle className="h-5 w-5 text-green-500" />
              <span className="text-xs">WhatsApp</span>
            </Button>

            {/* Facebook */}
            <Button
              variant="outline"
              className="h-16 flex-col gap-2 hover:bg-blue-600/10 hover:border-blue-600/50"
              onClick={handleFacebook}
              data-testid="share-facebook"
            >
              <Facebook className="h-5 w-5 text-blue-500" />
              <span className="text-xs">Facebook</span>
            </Button>

            {/* X / Twitter */}
            <Button
              variant="outline"
              className="h-16 flex-col gap-2 hover:bg-foreground/10"
              onClick={handleTwitter}
              data-testid="share-twitter"
            >
              <Twitter className="h-5 w-5" />
              <span className="text-xs">X</span>
            </Button>

            {/* Telegram */}
            <Button
              variant="outline"
              className="h-16 flex-col gap-2 hover:bg-sky-500/10 hover:border-sky-500/50"
              onClick={handleTelegram}
              data-testid="share-telegram"
            >
              <Send className="h-5 w-5 text-sky-500" />
              <span className="text-xs">Telegram</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareModal;
